/**
 * Admin sidebar (minimal counterpart of web/src/components/navigation/Sidebar.tsx).
 *
 * Internal tool: the sidebar is persistent (no mobile drawer in v1). Labeled
 * with aria-label for screen readers.
 */
import { NavLink, useParams } from 'react-router-dom';
import { getAdminNav } from '@/config/navigation';
import { useAdminRole } from '@/auth/AdminRoleContext';
import { BrandMark } from './BrandMark';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

export function AdminSidebar() {
  const { role } = useAdminRole();
  // The open-account item is derived from the URL param, never nav state.
  const { accountID } = useParams<{ accountID: string }>();
  const items = getAdminNav({ role, openAccountID: accountID });

  return (
    <aside
      className="hidden h-screen w-60 shrink-0 flex-col border-r bg-surface md:flex"
      aria-label="Admin navigation"
    >
      <div className="flex h-14 items-center px-4">
        <BrandMark />
      </div>
      <Separator />
      <nav className="flex-1 overflow-y-auto p-3">
        <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Console
        </p>
        <ul className="space-y-1">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  title={item.description}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.to.startsWith('/accounts/') ? (
                    <span className="truncate font-mono text-[10px] text-muted-foreground">
                      {accountID}
                    </span>
                  ) : null}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
